import { useState } from 'react'
import { Dialog, DialogContent } from '@mui/material'
import { useTranslation } from 'react-i18next'
import type { GridRowParams } from '@mui/x-data-grid'
import { type GridRenderCellParams } from '@mui/x-data-grid/models/params/gridCellParams'
import { DeleteIcon, PlusIcon } from '@dhl-ui/icons'
import { DialogTitleClosable, type GridColDef, Pagination } from '@dhl/components'
import { ConfirmationDialog } from '@dhl/confirmation-dialog'
import { useAssignBillingUnitsToUser } from '@dhl/mdp-api'
import { Box, Button, Card, Divider, H5, IconButton } from '@dhl-ui/components'

import { type AdminBillingUnit } from '~/modules/adminusers/model'
import { AdminBillingUnitsAssignDialog } from '~/modules/adminusers/components/AdminBillingUnitsAssignDialog'
import { AdminDataGrid, useNotificationMessage } from '~/common'
import { useAdminBillingUnits, useBillingUnits } from '~/modules/billingunits/hooks'

type Props = {
  adminUserId: string
  adminUserName: string
}

export const AdminBillingUnitsCard = ({ adminUserId, adminUserName }: Props) => {
  const { t } = useTranslation()
  const { showError, showSuccess } = useNotificationMessage()
  const [open, setOpen] = useState<boolean>(false)
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false)
  const [page, setPage] = useState<number>(0)
  const [pageSize, setPageSize] = useState<number>(10)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [idToBeRemoved, setIdToBeRemoved] = useState<string | undefined>(undefined)
  const [nameToBeRemoved, setNameToBeRemoved] = useState<string | undefined>(undefined)
  const { billingUnits: adminBillingUnits, isLoading, refetch } = useAdminBillingUnits(adminUserId)
  const { billingUnits: currentUserBillingUnits } = useBillingUnits()
  const { mutate: assignBillingUnitsToUser } = useAssignBillingUnitsToUser()

  const handleDeleteBillingUnit = (id: string, name: string) => {
    setIdToBeRemoved(id)
    setNameToBeRemoved(name)
    setConfirmOpen(true)
  }

  const handleDeleteBillingUnits = () => {
    setIdToBeRemoved(undefined)
    setNameToBeRemoved(undefined)
    setConfirmOpen(true)
  }

  const renderCellDelete = (params: GridRenderCellParams<AdminBillingUnit>) => {
    const { id, name } = params.row
    return (
      <IconButton
        disabled={!currentUserBillingUnits.map((b) => b.id).includes(id)}
        onClick={() => handleDeleteBillingUnit(id, name)}
        aria-label="delete billing unit"
      >
        <DeleteIcon data-testid="delete-billingunits" />
      </IconButton>
    )
  }

  const deleteBillingUnits = () => {
    const newAdminBillingUnits = adminBillingUnits
      .map((b) => b.id)
      .filter((id) => (idToBeRemoved ? id !== idToBeRemoved : !selectedIds.includes(id)))
    assignBillingUnitsToUser(
      { adminUserId, data: newAdminBillingUnits },
      {
        onSuccess: () => {
          setConfirmOpen(false)
          refetch()
          showSuccess(t('admin.billingUnitsPage.deleteBillingunitSuccess'))
        },
        onError: (response) => {
          showError(response?.message ?? t('admin.errorMessage'))
        },
      }
    )
  }

  const columns: GridColDef<AdminBillingUnit>[] = [
    {
      field: 'id',
    },
    {
      field: 'name',
      headerName: t('admin.billingUnitsPage.billingUnitsOverview.name'),
      flex: 1.5,
    },
    {
      field: 'cccId',
      headerName: t('admin.billingUnitsPage.billingUnitsOverview.identifier'),
      flex: 1,
    },
    {
      field: 'actions',
      headerName: t('admin.billingUnitsPage.billingUnitsOverview.actions'),
      sortable: false,
      flex: 0,
      renderCell: renderCellDelete,
      disableReorder: true,
      disableExport: true,
      resizable: false,
      filterable: false,
      editable: false,
    },
  ]

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleCloseConfirm = () => {
    setConfirmOpen(false)
  }

  return (
    <>
      <Card>
        <Card.Header
          title={t('admin.billingUnitsPage.billingUnitsOverview.title')}
          endNode={
            <>
              {selectedIds.length > 0 && (
                <Button
                  data-testid="remove-billing-units"
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteBillingUnits}
                  sx={{ mr: 1 }}
                >
                  {t('admin.billingUnitsPage.removeAdminBillingUnit')}
                </Button>
              )}
              <Button
                data-testid="add-billing-unit"
                variant="contained"
                color="primary"
                size="small"
                startIcon={<PlusIcon />}
                onClick={handleOpen}
              >
                {t('admin.billingUnitsPage.billingUnitsOverview.addBillingunitButtonTitle')}
              </Button>
            </>
          }
        />
        <Card.Content>
          <AdminDataGrid
            initialState={{
              columns: { columnVisibilityModel: { id: false } },
              sorting: { sortModel: [{ field: 'cccId', sort: 'asc' }] },
            }}
            onSortModelChange={() => {
              setPage(0)
            }}
            isRowSelectable={(params: GridRowParams<AdminBillingUnit>) =>
              currentUserBillingUnits.map((b) => b.id).includes(params.row.id)
            }
            loading={isLoading}
            data-testid="admin-billing-units-table"
            autoHeight
            rows={adminBillingUnits}
            columns={columns}
            paginationModel={{
              page,
              pageSize,
            }}
            checkboxSelection={true}
            onRowSelectionModelChange={(newSelectionModel) => {
              setSelectedIds(newSelectionModel as string[])
            }}
            rowHeight={48}
            columnHeaderHeight={48}
            disableRowSelectionOnClick
            disableColumnFilter
            hideFooter
          />
          <Box px={2} py={1}>
            <Pagination
              page={page}
              count={adminBillingUnits.length}
              onPageChange={(_, page) => setPage(page)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={setPageSize}
            />
          </Box>
        </Card.Content>
      </Card>
      {open && (
        <Dialog data-testid="existing-billing-units-dialog" open={true} onClose={handleClose} fullWidth>
          <Box>
            <DialogTitleClosable onClose={handleClose}>
              <H5>{t('admin.billingUnitsPage.billingUnitsOverview.title')}</H5>
            </DialogTitleClosable>
          </Box>
          <Divider />
          <DialogContent>
            <AdminBillingUnitsAssignDialog
              adminUserId={adminUserId}
              adminBillingUnits={adminBillingUnits}
              onRefetch={refetch}
              onClose={handleClose}
            />
          </DialogContent>
        </Dialog>
      )}
      {confirmOpen && (
        <ConfirmationDialog
          titleText={
            idToBeRemoved
              ? t('admin.billingUnitsPage.removeAdminBillingUnit')
              : t('admin.billingUnitsPage.removeAdminBillingUnits')
          }
          acceptText={
            idToBeRemoved
              ? t('admin.billingUnitsPage.removeAdminBillingUnit')
              : t('admin.billingUnitsPage.removeAdminBillingUnits')
          }
          onAccept={deleteBillingUnits}
          onReject={handleCloseConfirm}
          onClose={handleCloseConfirm}
          open
        >
          {idToBeRemoved
            ? t('admin.billingUnitsPage.confirmDeleteAdminBillingUnit', {
                billingUnitName: nameToBeRemoved,
                userName: adminUserName,
              })
            : t('admin.billingUnitsPage.confirmDeleteAdminBillingUnits', {
                count: selectedIds.length,
                userName: adminUserName,
              })}
        </ConfirmationDialog>
      )}
    </>
  )
}
